import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { productionOverviewPath } from "@/lib/productionNavigation";
import { Loader2 } from "lucide-react";

/**
 * Co-producer entry point. The producer shares a secret link
 * (/co-producer/access?token=...) plus a password. Here the co-producer enters
 * the password; the co-producer-login edge function verifies it and returns a
 * session we adopt before sending them straight into their one production.
 */
export default function CoProducerAccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid link");
      navigate("/producer-login");
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("co-producer-login", {
        body: { token, password },
      });

      if (error || !data?.success || !data?.session) {
        throw new Error("Invalid link or password");
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      if (sessionError) throw sessionError;

      toast.success("Welcome! Opening your production…");
      navigate(productionOverviewPath(data.briefing_id), { replace: true });
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Invalid link or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Co-producer access</CardTitle>
          <CardDescription>Enter the password you received to open the production.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              autoFocus
            />
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening…
                </>
              ) : (
                "Open production"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
